/**
 * AddressAutocompleteInput
 *
 * A text input that shows an inline address autocomplete dropdown by querying
 * GET /landlord/api/units/address-autocomplete?q=. Debounces 300 ms, fires on
 * 3+ characters, and calls onSuggestionSelect with the chosen suggestion.
 * Selecting a suggestion does NOT fill address2 — the caller decides which
 * fields to update.
 */

import { useState, useEffect, useRef } from 'react';
import type { AddressSuggestion } from '@resida/api-types/leasing';

export type { AddressSuggestion };

interface AddressAutocompleteInputProps {
  id: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  onSuggestionSelect: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'data-testid'?: string;
}

export function AddressAutocompleteInput({
  id,
  name,
  value,
  onChange,
  onSuggestionSelect,
  placeholder,
  className,
  required,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'data-testid': dataTestId,
}: AddressAutocompleteInputProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True only while the current value came from the user typing in this input. Suggestions
  // open in response to typing, never on mount with a pre-filled value (e.g. returning to a
  // wizard step) and never after a selection populated the value, where a reopened list would
  // cover the fields the user was just sent to.
  const userTypedRef = useRef(false);

  // Debounced fetch — fires 300 ms after the user stops typing
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (!userTypedRef.current) return;

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const res = await fetch(
          `/landlord/api/units/address-autocomplete?q=${encodeURIComponent(value)}`,
          { credentials: 'include' }
        );
        if (!res.ok) {
          setSuggestions([]);
          setIsOpen(false);
          setHighlightedIndex(-1);
          return;
        }
        const data = await res.json() as { suggestions: AddressSuggestion[] };
        setSuggestions(data.suggestions ?? []);
        setIsOpen((data.suggestions ?? []).length > 0);
        setHighlightedIndex(-1);
      } catch {
        // Silently close — form still works without suggestions
        setSuggestions([]);
        setIsOpen(false);
        setHighlightedIndex(-1);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value]);

  const handleSelect = (suggestion: AddressSuggestion) => {
    userTypedRef.current = false;
    onSuggestionSelect(suggestion);
    setSuggestions([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleBlur = () => {
    // Small delay so onMouseDown on suggestion items fires before blur closes dropdown
    setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 150);
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      const highlighted = highlightedIndex >= 0 ? suggestions[highlightedIndex] : undefined;
      if (highlighted) {
        e.preventDefault();
        handleSelect(highlighted);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const activedescendant =
    isOpen && highlightedIndex >= 0
      ? `${id}-suggestion-${highlightedIndex}`
      : undefined;

  return (
    <div className="relative">
      {/* Visually-hidden live region announces suggestion count to screen readers */}
      <span
        role="status"
        aria-live="polite"
        className="sr-only"
      >
        {isOpen && suggestions.length > 0
          ? `${suggestions.length} suggestion${suggestions.length === 1 ? '' : 's'} available`
          : ''}
      </span>
      <input
        id={id}
        name={name}
        type="text"
        value={value}
        onChange={(e) => {
          userTypedRef.current = true;
          onChange(e.target.value);
        }}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        required={required}
        aria-required={required ? true : undefined}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        data-testid={dataTestId}
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={isOpen ? `${id}-suggestions` : undefined}
        aria-activedescendant={activedescendant}
        autoComplete="off"
      />
      {/* Subtle loading indicator */}
      {isLoading && (
        <span
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground"
          aria-hidden="true"
        >
          ···
        </span>
      )}
      {isOpen && suggestions.length > 0 && (
        <ul
          id={`${id}-suggestions`}
          role="listbox"
          aria-label="Address suggestions"
          className="absolute top-full left-0 right-0 z-50 bg-card border border-border rounded-md shadow-md mt-1 max-h-48 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <li
              key={i}
              id={`${id}-suggestion-${i}`}
              role="option"
              aria-selected={i === highlightedIndex}
              data-testid={`unit-address-suggestion-${i}`}
              className={`px-3 py-3 text-sm cursor-pointer truncate min-w-0 hover:bg-muted${i === highlightedIndex ? ' bg-muted' : ''}`}
              onMouseDown={(e) => {
                // preventDefault prevents the input blur from closing the dropdown before the click fires
                e.preventDefault();
                handleSelect(s);
              }}
            >
              {s.displayLabel}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AddressAutocompleteInput;
